import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const daysUntil = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

const SupplyBar = ({ level }) => {
  const color = level < 20 ? 'var(--cr-red)' : level < 40 ? 'var(--cr-amber)' : 'var(--cr-green)';
  return (
    <div style={{ height: 4, background: 'var(--border-color)', borderRadius: 2, overflow: 'hidden', marginTop: 3 }}>
      <div style={{ width: `${level}%`, height: '100%', background: color, borderRadius: 2 }} />
    </div>
  );
};

const HorseCard = ({ horse, vaccines, wormers }) => {
  const navigate = useNavigate();
  const nextVaccine = vaccines?.filter(v => v.next_due_date).sort((a, b) => new Date(a.next_due_date) - new Date(b.next_due_date))[0];
  const nextWormer = wormers?.filter(w => w.next_due_date).sort((a, b) => new Date(a.next_due_date) - new Date(b.next_due_date))[0];
  const vacDays = daysUntil(nextVaccine?.next_due_date);
  const wormDays = daysUntil(nextWormer?.next_due_date);
  const hasAlert = (vacDays !== null && vacDays <= 14) || (wormDays !== null && wormDays <= 14);

  return (
    <div className="card" style={{ marginBottom: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
      onClick={() => navigate(`/horses/${horse.id}`)}>
      <div className="avatar avatar-lg">
        {horse.avatar_url ? <img src={horse.avatar_url} alt={horse.name} /> : <span style={{ fontSize: 26 }}>🐴</span>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <h3 style={{ margin: 0 }}>{horse.name}</h3>
          {hasAlert && <span className="badge badge-amber" style={{ fontSize: 10 }}>Alert</span>}
        </div>
        <p style={{ fontSize: 13, margin: 0, color: 'var(--text-secondary)' }}>
          {[horse.breed, horse.discipline].filter(Boolean).join(' · ') || horse.gender}
        </p>
      </div>
      <span style={{ color: 'var(--text-muted)', fontSize: 22 }}>›</span>
    </div>
  );
};

const TabIcon = ({ name }) => {
  const p = {
    horses: <path d="M4 20c0-4 2-6 4-8l1-4 3-3c2-1 4-1 6 0l2 3-2 2-2-1-1 2c1 2 1 4 0 6l-1 3" />,
    supplies: <><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><path d="M12 12l8-4.5M12 12v9M12 12L4 7.5"/></>,
    documents: <><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></>,
  }[name];
  return <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: '-3px', marginRight: 6 }}>{p}</svg>;
};

const TILE_COLORS = {
  'Daily Horse Check': ['#8B1E52', '#6B0F3D', '#4B002B'],
  'Bulk Health Entry': ['#777777', '#5F5F5F', '#454545'],
  'Appointments': ['#6B1A45', '#4B002B', '#3A0121'],
  'Calendar & Booking': ['#54759A', '#3E5C7E', '#2E4763'],
  'Calendar and Booking': ['#54759A', '#3E5C7E', '#2E4763'],
  'Documents': ['#A17A4A', '#8A6136', '#6E4C26'],
  'Feed stacks': ['#2A7E62', '#175E48', '#0E4634'],
  'Hay stacks': ['#C69335', '#A87A1F', '#8A6212'],
  'Trailers': ['#4E6E8E', '#3D5A80', '#2C4763'],
  'Show Center': ['#BC4A70', '#A03457', '#822342'],
  'Supplement stacks': ['#BC603A', '#A34A26', '#87381A'],
  'Supplies': ['#52102F', '#33001D', '#240013'],
  'Weather Watch': ['#54759A', '#3E5C7E', '#2E4763'],
};
const _sv = (paths) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths}</svg>
);
const TILE_ICONS = {
  'Daily Horse Check': _sv(<><rect x="4" y="3" width="16" height="18" rx="3"/><path d="M9 3h6v4H9z"/><path d="M8 13l2 2 5-5"/></>),
  'Bulk Health Entry': _sv(<><path d="M12 2v20M2 12h20"/><circle cx="12" cy="12" r="8"/></>),
  'Appointments': _sv(<><rect x="3" y="4" width="18" height="17" rx="2"/><path d="M16 2v4M8 2v4M3 9h18"/><circle cx="15.5" cy="15.5" r="3"/><path d="M15.5 14v1.5l1 1"/></>),
  'Calendar & Booking': _sv(<><rect x="3" y="4" width="18" height="17" rx="2"/><path d="M16 2v4M8 2v4M3 9h18"/><path d="M8 13h2v2H8zM14 13h2v2h-2zM8 17h2v2H8z"/></>),
  'Calendar and Booking': _sv(<><rect x="3" y="4" width="18" height="17" rx="2"/><path d="M16 2v4M8 2v4M3 9h18"/><path d="M8 13h2v2H8zM14 13h2v2h-2zM8 17h2v2H8z"/></>),
  'Documents': _sv(<><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z"/><path d="M14 2v5h5"/><path d="M9 13h6M9 17h6"/></>),
  'Feed stacks': _sv(<><path d="M12 21v-8"/><path d="M12 13C12 9 9 7 5 7c0 4 3 6 7 6z"/><path d="M12 13c0-4 3-6 7-6 0 4-3 6-7 6z"/></>),
  'Hay stacks': _sv(<><path d="M12 3l9 5-9 5-9-5z"/><path d="M3 13l9 5 9-5"/></>),
  'Trailers': _sv(<><path d="M12 3l9 5-9 5-9-5z"/><path d="M3 13l9 5 9-5"/></>),
  'Trailers': _sv(<><rect x="2" y="8" width="15" height="8" rx="1"/><path d="M17 12h5M20 12v2"/><circle cx="7" cy="18" r="2"/><circle cx="13" cy="18" r="2"/><path d="M5 8V6h9v2"/></>),
  'Show Center': _sv(<><path d="M8 21h8M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0z"/><path d="M7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4"/></>),
  'Supplement stacks': _sv(<><rect x="6" y="4" width="12" height="17" rx="2"/><path d="M9 4a3 3 0 0 1 6 0"/><path d="M9 11h6M9 15h6M9 18h4"/></>),
  'Supplies': _sv(<><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><path d="M12 12l8-4.5M12 12v9M12 12L4 7.5"/></>),
  'Weather Watch': _sv(<><path d="M7 17a4 4 0 1 1 .6-7.96A5.5 5.5 0 0 1 18 8.3 4.2 4.2 0 0 1 18.5 16"/><path d="M12 13l-2 4h4l-2 4"/></>),
};
const ToolCard = ({ icon, title, desc, onClick }) => {
  const c = TILE_COLORS[title] || ['#6B1A45', '#4B002B', '#3A0121'];
  const tileIcon = TILE_ICONS[title];
  return (
  <button className="card" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', border: '1px solid var(--border-color)', textAlign: 'left' }}
    onClick={onClick}>
    <span style={{ width: 40, height: 40, borderRadius: 11, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5EBDD', background: 'linear-gradient(165deg, ' + c[0] + ' 0%, ' + c[1] + ' 55%, ' + c[2] + ' 100%)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.35), inset 0 -2px 3px rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.22)' }}>{tileIcon || <span style={{ fontSize: 22 }}>{icon}</span>}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{desc}</div>
    </div>
    <span style={{ color: '#A97B4F', fontSize: 20 }}>›</span>
  </button>
  );
};

const HorsesPage = () => {
  const navigate = useNavigate();
  const [horses, setHorses] = useState([]);
  const [healthData, setHealthData] = useState({});
  const [supplies, setSupplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('horses');
  const [dailyCheckSummary, setDailyCheckSummary] = useState({ total: 0, completed: 0 });

  useEffect(() => {
    const load = async () => {
      try {
        const [horsesRes, suppliesRes] = await Promise.all([
          api.get('/horses'),
          api.get('/shopping/supplies'),
        ]);
        setHorses(horsesRes.data);
        setSupplies(suppliesRes.data);
        // Load health data for each horse
        const hData = {};
        await Promise.all(horsesRes.data.map(async (horse) => {
          const [vRes, wRes] = await Promise.all([
            api.get(`/health/${horse.id}/vaccines`),
            api.get(`/health/${horse.id}/wormers`),
          ]);
          hData[horse.id] = { vaccines: vRes.data, wormers: wRes.data };
        }));
        setHealthData(hData);
        try {
          const d = new Date();
          const date = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          const checkRes = await api.get('/horse-checks/today', { params: { date, period: 'morning' } });
          setDailyCheckSummary({ total: checkRes.data.horses?.length || 0, completed: checkRes.data.completed || 0 });
        } catch {}
      } catch { toast.error('Failed to load horses'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const lowSupplies = supplies.filter(s => s.current_level <= s.low_threshold);

  return (
    <div>
      <TopBar
        title="My horses"
        subtitle={`${horses.length} horse${horses.length !== 1 ? 's' : ''} · Horse care, community & marketplace`}
        rightAction={
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', fontSize: 12 }}
            onClick={() => navigate('/horses/add')}>
            + Add
          </button>
        }
      />

      <div className="tab-bar">
        {['horses', 'supplies'].map(tab => (
          <div key={tab} className={`tab-item ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
            style={{ textTransform: 'capitalize' }}>
            {tab === 'supplies' ? `Supplies${lowSupplies.length > 0 ? ` (${lowSupplies.length})` : ''}` : 'Horses'}
          </div>
        ))}
      </div>

      <div className="page-content">
        {loading && <div className="loading-screen"><div className="spinner" /></div>}

        {!loading && activeTab === 'horses' && (
          <>
            {horses.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🐴</div>
                <h3>No horses yet</h3>
                <p>Add your first horse to get started tracking their health, calendar, and more</p>
                <button className="btn btn-primary" onClick={() => navigate('/horses/add')}>Add your first horse</button>
              </div>
            ) : (
              horses.map(horse => (
                <HorseCard key={horse.id} horse={horse} vaccines={healthData[horse.id]?.vaccines} wormers={healthData[horse.id]?.wormers} />
              ))
            )}
            {horses.length > 0 && (
              <button className="btn btn-secondary btn-full" onClick={() => navigate('/horses/add')}>+ Add another horse</button>
            )}
            {(horses.length > 0 || dailyCheckSummary.total > 0) && (
              <>
                <div className="section-label" style={{ marginTop: 16 }}>Horse Tools</div>
                <ToolCard icon="✓" title="Daily Horse Check"
                  desc={`Record routine checks & concerns · ${dailyCheckSummary.completed} of ${dailyCheckSummary.total || horses.length} checked today`}
                  onClick={() => navigate('/horses/daily-check')} />
                <ToolCard icon="💉" title="Bulk Health Entry"
                  desc="Worm or vaccinate multiple horses"
                  onClick={() => navigate('/health/bulk')} />
                <ToolCard icon="🔨" title="Appointments"
                  desc="Schedule farrier, bodywork, chiropractic, saddle fitting, training, hauling, and more"
                  onClick={() => navigate('/appointments')} />
                <ToolCard icon="🗓️" title="Calendar & Booking"
                  desc="Lessons, trainer routes, group capacity, releases, and travel planning"
                  onClick={() => navigate('/calendar')} />
                <ToolCard icon="📁" title="Documents"
                  desc="Coggins, health certificates, insurance, registration papers"
                  onClick={() => navigate('/documents')} />
                <ToolCard icon="🌾" title="Feed stacks"
                  desc="Apply a grain/feed program to multiple horses"
                  onClick={() => navigate('/feed-stacks')} />
                <ToolCard icon="🟩" title="Hay stacks"
                  desc="Apply hay plans to multiple horses"
                  onClick={() => navigate('/hay-stacks')} />
                <ToolCard icon="🏆" title="Show Center"
                  desc="Credentials, show packets, secretaries"
                  onClick={() => navigate('/shows')} />
                <ToolCard icon="📋" title="Supplement stacks"
                  desc="Apply a supplement program to multiple horses"
                  onClick={() => navigate('/supplement-stacks')} />
                <ToolCard icon="🧴" title="Supplies"
                  desc="Track and reorder horse supplies"
                  onClick={() => navigate('/supplies')} />
                <ToolCard icon="🌦️" title="Weather Watch"
                  desc="Set alerts for heat, cold, rain, storms, and turnout decisions"
                  onClick={() => navigate('/weather-watch')} />
              </>
            )}
          </>
        )}

        {!loading && activeTab === 'supplies' && (
          <>
            {lowSupplies.length > 0 && (
              <>
                <div className="section-label" style={{ color: 'var(--cr-red)' }}>⚠️ Running low</div>
                {lowSupplies.map(s => (
                  <div key={s.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{s.product_name}</div>
                      <SupplyBar level={s.current_level} />
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{s.current_level}% remaining{s.estimated_days_remaining ? ` · ~${s.estimated_days_remaining}d left` : ''}</div>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={() => navigate(`/shop?q=${encodeURIComponent(s.product_name)}`)}>Reorder</button>
                  </div>
                ))}
              </>
            )}

            <div className="section-label">All supplies</div>
            {supplies.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🧴</div>
                <h3>No supplies tracked</h3>
                <p>Add supplies to track levels and get reorder reminders</p>
              </div>
            ) : (
              supplies.map(s => (
                <div key={s.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{s.product_name}</div>
                    <SupplyBar level={s.current_level} />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{s.current_level}% remaining</div>
                  </div>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/shop?q=${encodeURIComponent(s.product_name)}`)}>Shop</button>
                </div>
              ))
            )}
            <button className="btn btn-secondary btn-full" style={{ marginTop: 8 }} onClick={() => navigate('/shop')}>+ Add supply to track</button>
          </>
        )}
      </div>
    </div>
  );
};

export default HorsesPage;
